import { Box, type SxProps, type Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { SEMANTIC_COLOR_VARS, type SemanticColor } from './semanticColor';

interface PillProps {
  children: ReactNode;
  color?: SemanticColor | 'neutral';
  /** Small static dot before the label (e.g. category color key). For a
   * pulsing "live" dot, compose with <LiveDot /> as a child instead. */
  dot?: boolean;
  /** Render the label in the app's mono font — on by default since pills are
   * almost always counts/telemetry (mock's dominant `font-mono` usage). */
  mono?: boolean;
  sx?: SxProps<Theme>;
}

/** Small tinted pill for counts and status labels (e.g. "11 Pending", "84% Score"). */
export function Pill({ children, color = 'neutral', dot, mono = true, sx }: PillProps) {
  const vars = color === 'neutral' ? null : SEMANTIC_COLOR_VARS[color];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.1,
        py: 0.3,
        borderRadius: 'var(--radius-full)',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.6,
        fontFamily: mono ? 'var(--font-mono)' : undefined,
        color: vars ? vars.main : 'text.secondary',
        backgroundColor: vars ? vars.soft : 'action.hover',
        border: '1px solid',
        borderColor: vars ? vars.line : 'divider',
        ...sx,
      }}
    >
      {dot && (
        <Box
          component="span"
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: vars ? vars.main : 'text.disabled',
            flexShrink: 0,
          }}
        />
      )}
      {children}
    </Box>
  );
}
